import type { FormEvent, ReactNode } from 'react'
import type { GetServerSideProps } from 'next'
import type { FieldErrors } from '@/types'

import Link from 'next/link'
import { useState } from 'react'
import { useRouter } from 'next/router'
import { Check, LoaderCircle, UserRoundPlus } from 'lucide-react'
import { toast } from 'sonner'
import { cn } from '@/lib/utils'
import { Seo } from '@/components/generals/seo'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Field, fieldAria } from '@/components/ui/field'
import { ApiError, apiRequest, errorMessage } from '@/lib/api'
import { getSessionUser, safeNextPath } from '@/lib/server/auth'
import { firstParam } from '@/lib/server/http'
import { LIMITS } from '@/constants/finance'
import AuthLayout from '@/components/auth/auth-layout'
import PasswordInput from '@/components/auth/password-input'

import {
    validateEmail,
    validateName,
    validateNewPassword
} from '@/lib/validation'

interface RegisterPageProps {
    next: string
}

type RegisterField = 'name' | 'email' | 'password' | 'confirmPassword'

const FIELD_ORDER: RegisterField[] = ['name', 'email', 'password', 'confirmPassword']

export default function RegisterPage({ next }: RegisterPageProps): ReactNode {
    const router = useRouter()
    const [values, setValues] = useState<Record<RegisterField, string>>({ name: '', email: '', password: '', confirmPassword: '' })
    const [errors, setErrors] = useState<FieldErrors<RegisterField>>({})
    const [formError, setFormError] = useState('')
    const [submitting, setSubmitting] = useState(false)

    const update = (field: RegisterField, value: string): void => {
        setValues(current => ({ ...current, [field]: value }))
        if (errors[field]) setErrors(current => ({ ...current, [field]: undefined }))
    }

    const rules = [
        { label: `Minimal ${LIMITS.passwordMin} karakter`, met: values.password.length >= LIMITS.passwordMin },
        { label: 'Mengandung huruf', met: /[A-Za-z]/.test(values.password) },
        { label: 'Mengandung angka', met: /\d/.test(values.password) }
    ]

    const submit = async (event: FormEvent<HTMLFormElement>): Promise<void> => {
        event.preventDefault()
        if (submitting) return

        const nextErrors: FieldErrors<RegisterField> = {}
        const nameError = validateName(values.name)
        const emailError = validateEmail(values.email)
        const passwordError = validateNewPassword(values.password)

        if (nameError) nextErrors.name = nameError
        if (emailError) nextErrors.email = emailError
        if (passwordError) nextErrors.password = passwordError
        if (!passwordError && values.password !== values.confirmPassword) nextErrors.confirmPassword = 'Konfirmasi kata sandi tidak sama.'

        setErrors(nextErrors)
        setFormError('')

        const firstInvalid = FIELD_ORDER.find(field => nextErrors[field])

        if (firstInvalid) {
            document.getElementById(`register-${firstInvalid}`)?.focus()
            return
        }

        setSubmitting(true)

        try {
            const data = await apiRequest<{ redirectTo: string }>('/api/auth/register', {
                method: 'POST',
                body: { ...values, next }
            })

            toast.success('Akunmu berhasil dibuat. Selamat mencatat!')
            await router.replace(data.redirectTo)
        }
        catch (error) {
            if (error instanceof ApiError && Object.keys(error.errors).length > 0) {
                setErrors(error.errors as FieldErrors<RegisterField>)
                const field = FIELD_ORDER.find(key => error.errors[key])
                if (field) document.getElementById(`register-${field}`)?.focus()
            }
            else {
                setFormError(errorMessage(error))
            }

            setSubmitting(false)
        }
    }

    return (
        <>
            <Seo
                title='Daftar'
                description='Buat akun Daily Financial Recording System gratis dan mulai catat keuangan harianmu.'
            />

            <AuthLayout
                title='Buat akun baru'
                description='Mulai catat pemasukan dan pengeluaranmu. Kategori dasar sudah kami siapkan.'
                footer={(
                    <>
                        Sudah punya akun?{' '}
                        <Link
                            href={next !== '/' ? `/masuk?next=${encodeURIComponent(next)}` : '/masuk'}
                            className='font-semibold text-navy underline-offset-4 hover:underline'
                        >
                            Masuk di sini
                        </Link>
                    </>
                )}
            >
                <form noValidate onSubmit={event => void submit(event)} className='flex flex-col gap-5'>
                    {formError && (
                        <div role='alert' className='rounded-lg border border-destructive/20 bg-expense-soft px-4 py-3 text-sm font-medium text-expense-strong'>
                            {formError}
                        </div>
                    )}

                    <Field id='register-name' label='Nama lengkap' error={errors.name}>
                        <Input
                            id='register-name'
                            autoComplete='name'
                            placeholder='Contoh: Rina Kartika'
                            value={values.name}
                            maxLength={LIMITS.nameMax}
                            onChange={event => update('name', event.target.value)}
                            {...fieldAria('register-name', errors.name)}
                        />
                    </Field>

                    <Field id='register-email' label='Email' error={errors.email}>
                        <Input
                            id='register-email'
                            type='email'
                            inputMode='email'
                            autoComplete='email'
                            placeholder='nama@email.com'
                            value={values.email}
                            onChange={event => update('email', event.target.value)}
                            {...fieldAria('register-email', errors.email)}
                        />
                    </Field>

                    <Field id='register-password' label='Kata sandi' error={errors.password}>
                        <PasswordInput
                            id='register-password'
                            autoComplete='new-password'
                            placeholder='Buat kata sandi'
                            value={values.password}
                            maxLength={LIMITS.passwordMax}
                            onChange={event => update('password', event.target.value)}
                            {...fieldAria('register-password', errors.password)}
                        />

                        <ul aria-label='Syarat kata sandi' className='grid grid-cols-1 gap-1.5 sm:grid-cols-3 sm:gap-2'>
                            {rules.map(rule => (
                                <li
                                    key={rule.label}
                                    className={cn('flex items-center gap-1.5 text-xs', rule.met ? 'font-medium text-income-strong' : 'text-muted-foreground')}
                                >
                                    <span className={cn('flex size-4 items-center justify-center rounded-full', rule.met ? 'bg-income-soft' : 'bg-muted')}>
                                        <Check aria-hidden='true' className={cn('size-3', rule.met ? 'opacity-100' : 'opacity-30')} />
                                    </span>

                                    {rule.label}

                                    <span className='sr-only'>
                                        {rule.met ? '(terpenuhi)' : '(belum terpenuhi)'}
                                    </span>
                                </li>
                            ))}
                        </ul>
                    </Field>

                    <Field id='register-confirmPassword' label='Ulangi kata sandi' error={errors.confirmPassword}>
                        <PasswordInput
                            id='register-confirmPassword'
                            autoComplete='new-password'
                            placeholder='Ketik ulang kata sandi'
                            value={values.confirmPassword}
                            maxLength={LIMITS.passwordMax}
                            onChange={event => update('confirmPassword', event.target.value)}
                            {...fieldAria('register-confirmPassword', errors.confirmPassword)}
                        />
                    </Field>

                    <Button type='submit' size='lg' disabled={submitting} className='mt-1 w-full'>
                        {submitting ? <LoaderCircle className='animate-spin' /> : <UserRoundPlus />}
                        {submitting ? 'Membuat akun...' : 'Buat Akun'}
                    </Button>
                </form>
            </AuthLayout>
        </>
    )
}

export const getServerSideProps: GetServerSideProps<RegisterPageProps> = async ({ req, query }) => {
    const next = safeNextPath(firstParam(query.next))
    const user = await getSessionUser(req).catch(() => null)

    if (user) return { redirect: { destination: next, permanent: false } }

    return { props: { next } }
}
